import React from 'react';
import { Card, Grid, NumberInput, Select, Text, TextInput, Title } from '@mantine/core';
import type { ICotizacionServicios } from '../../../../modelos/CotizadorServicios/entidades/ICotizacionServicios';
import type { IOpcionSelectServicio } from '../../../../modelos/CotizadorServicios/entidades/IOpcionesCotizacionServicios';

interface PasoDatosGeneralesVistaProps {
    cotizacion: ICotizacionServicios;
    paises: IOpcionSelectServicio[];
    errores: Record<string, string>;
    soloLectura: boolean;
    alCambiar: <K extends keyof ICotizacionServicios>(campo: K, valor: ICotizacionServicios[K]) => void;
}

interface AjustesFamiliaProps {
    titulo: string;
    descuento: number;
    incremento: number;
    soloLectura: boolean;
    alCambiarDescuento: (valor: number) => void;
    alCambiarIncremento: (valor: number) => void;
}

/** Descuento e incremento comercial de una familia de equipos (porcentajes 0–100). */
const AjustesFamilia: React.FC<AjustesFamiliaProps> = ({ titulo, descuento, incremento, soloLectura, alCambiarDescuento, alCambiarIncremento }) => (
    <Card withBorder radius="md" p="md" h="100%">
        <Text fw={600} size="sm" mb="sm">{titulo}</Text>
        <Grid>
            <Grid.Col span={6}>
                <NumberInput size="xs" label="Descuento" min={0} max={100} allowNegative={false} suffix=" %" decimalScale={2} hideControls value={descuento} onChange={(v) => alCambiarDescuento(Number(v) || 0)} disabled={soloLectura} />
            </Grid.Col>
            <Grid.Col span={6}>
                <NumberInput size="xs" label="Incremento" min={0} allowNegative={false} suffix=" %" decimalScale={2} hideControls value={incremento} onChange={(v) => alCambiarIncremento(Number(v) || 0)} disabled={soloLectura} />
            </Grid.Col>
        </Grid>
    </Card>
);

/** Paso 1: datos del proyecto y ajustes comerciales por familia. */
export const PasoDatosGeneralesVista: React.FC<PasoDatosGeneralesVistaProps> = ({ cotizacion, paises, errores, soloLectura, alCambiar }) => (
    <Grid>
        <Grid.Col span={12}>
            <Title order={5} mb="xs">Información del Proyecto</Title>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
            <TextInput size="xs" withAsterisk label="Proyecto" maxLength={100} value={cotizacion.Proyecto} onChange={(e) => alCambiar('Proyecto', e.currentTarget.value)} error={errores.Proyecto} disabled={soloLectura} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
            <TextInput size="xs" withAsterisk label="Cliente" maxLength={100} value={cotizacion.Cliente} onChange={(e) => alCambiar('Cliente', e.currentTarget.value)} error={errores.Cliente} disabled={soloLectura} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
            <Select size="xs" withAsterisk label="País" data={paises} value={cotizacion.PaisId ? String(cotizacion.PaisId) : null} onChange={(v) => alCambiar('PaisId', v ? Number(v) : undefined)} error={errores.PaisId} disabled={soloLectura} searchable />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6, lg: 3 }}>
            <NumberInput size="xs" withAsterisk label="Año del contrato" min={2025} allowNegative={false} allowDecimal={false} hideControls value={cotizacion.Anio} onChange={(v) => alCambiar('Anio', Number(v) || 0)} error={errores.Anio} disabled={soloLectura} />
        </Grid.Col>

        <Grid.Col span={12} mt="md">
            <Title order={5} mb="xs">Descuentos e Incrementos</Title>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
            <AjustesFamilia
                titulo="Ascensores"
                descuento={cotizacion.DescuentoAscensores}
                incremento={cotizacion.IncrementoAscensores}
                soloLectura={soloLectura}
                alCambiarDescuento={(v) => alCambiar('DescuentoAscensores', v)}
                alCambiarIncremento={(v) => alCambiar('IncrementoAscensores', v)}
            />
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 6 }}>
            <AjustesFamilia
                titulo="Escaleras, Rampas y Andenes"
                descuento={cotizacion.DescuentoEscaleras}
                incremento={cotizacion.IncrementoEscaleras}
                soloLectura={soloLectura}
                alCambiarDescuento={(v) => alCambiar('DescuentoEscaleras', v)}
                alCambiarIncremento={(v) => alCambiar('IncrementoEscaleras', v)}
            />
        </Grid.Col>
    </Grid>
);
